import { LoginResponseBodyUserType } from "@intelligems/ig-types";
import { useMutation } from "@tanstack/react-query";

import { AI_ROUTES } from "../../routes/routes";
import { IgStorage } from "../../utils/igStorage";
import { api } from "../api";

export const switchOrganizationByAgenticLogicRule = async (
  token: string,
  agenticLogicRuleId: string
): Promise<LoginResponseBodyUserType> => {
  const resp = await api.post<LoginResponseBodyUserType>("/v2/login", {
    token,
    agenticLogicRuleId,
  });
  return resp.data;
};

export const getPostAgenticLogicRuleSwitchAction = (
  requestedRule: LoginResponseBodyUserType["requestedAgenticLogicRule"]
): "openAgenticLogic" | "reloadCurrentRoute" =>
  requestedRule?.isDeleted ? "openAgenticLogic" : "reloadCurrentRoute";

/**
 * Moves the session into the org that owns a rule and then reloads.
 *
 * The experience twin of this hook clears the redux slices and the query cache
 * by hand because it navigates in-app. Here the switch always leaves through
 * the browser: the rule link can be opened from anywhere in the admin, and a
 * full document load is the only thing that rebuilds every org-scoped store —
 * redux, Apollo and react-query alike — from the new token. It also keeps this
 * hook free of Router and redux context, which matters because it is called
 * from `useAgenticLogicRule`, i.e. from every consumer of a rule.
 */
export const useSwitchOrganizationByAgenticLogicRule = () => {
  return useMutation<
    LoginResponseBodyUserType,
    Error,
    { token: string; agenticLogicRuleId: string }
  >({
    mutationKey: [{ scope: "user" }],
    mutationFn: async (body) =>
      switchOrganizationByAgenticLogicRule(body.token, body.agenticLogicRuleId),
    onSuccess: (data) => {
      // eslint-disable-next-line react-compiler/react-compiler
      IgStorage.authToken = data.token;

      if (
        getPostAgenticLogicRuleSwitchAction(data.requestedAgenticLogicRule) ===
        "reloadCurrentRoute"
      ) {
        window.location.reload();
        return;
      }

      // The rule is gone, so the route that asked for it no longer resolves.
      window.location.assign(AI_ROUTES.AiAgenticLogic);
    },
  });
};
